import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { CellMeasurerCache } from 'react-virtualized';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import DraggableContentWrapper from './DraggableContentWrapper';
import { setIsAfterRecurringSectionExpanded } from '../../ducks/calendarActions';

const AfterRecurringHeader = styled('div')`
  flex-direction: column;
  display: flex;
  min-height: 48px;
  margin-bottom: 9px;
`;

const IconContainer = styled('div')`
   padding-left: ${(props) => (props.viewType === 'weekView' ? '10px' : '28px')};
  cursor: pointer;
  display: flex;
  padding-bottom: 8px;
  padding-top: 8px;
  background-color: #F8F8F8;
  border-radius: 4px;
  padding-left: ${(props) => props.addPaddingLeft && '36px'};
`;

const RecurringText = styled('div')`
  margin-left: 7px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const IconContainerWrapper = styled('div')`
  position: relative;
  background: #444444;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #444444;
  min-height: 25px;
  min-width: 25px;
  max-width: 25px;
  max-height: 25px;
`;

const BeforeRecurringWorkouts = styled('div')`
  display: flex;
  flex-direction: column;
  transition: max-height 0.3s ease;
  max-width: 100%;
  background-color: #F8F8F8;
  border-radius: 4px;
`;

const LineContainer = styled('div')`
  min-height: 32px;
  position: relative;
  flex-grow: 1;
  margin-left: 10px;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 2px;
    background-color: #444444;
  }
  width: 100%;
  overflow: hidden;
`;

const ExpandCollapseContainer = styled('div')`
  :hover {
    cursor: pointer;
    background: rgba(119, 167, 255, 0.2);
  }
  height: 32px;
  width: 32px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 10px;

  .expandCollapseIcon {
    fill: lightgray;
  }

  &:hover .expandCollapseIcon {
    fill: #77A7FF;
  }
`;

const AfterRecurringVirtualRow = React.memo(({
  measure,
  cache,
  date,
  dayKey,
  onDayExpand,
  isDayExpanded,
}) => {
  const dispatch = useDispatch();
  const afterTooltipId = 'after-expand-tooltip';
  const formatDate = (d) => `${moment(d).format('YYYY-MM-DD')}`;
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const isWeekView = viewType === 'weekView';
  const afterRecurringWorkouts = useSelector((state) => state.calendar.data.afterRecurringWorkouts);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const effectiveDate = date || selectedDate;
  const isAfterExpandedDayView = useSelector(
    (state) => state.calendar.data.isAfterRecurringSectionExpanded,
  );
  const isAfterExpandedWeekView = isAfterExpandedDayView[dayKey];
  const isAfterExpanded = viewType === 'weekView' ? isAfterExpandedWeekView : isAfterExpandedDayView;
  const [afterExpandHovering, setAfterExpandHovering] = useState(false);
  const weekViewHorizontallyExpandedDay = useSelector(
    (state) => state.calendar.data.weekViewExpandedDay,
  );
  const isRecurringRowInHorizontallyExpandedDay = dayKey === weekViewHorizontallyExpandedDay;

  return (
    <AfterRecurringHeader>
      <IconContainer
        onClick={() => {
          if (viewType === 'weekView') {
            dispatch(setIsAfterRecurringSectionExpanded({
              ...isAfterExpanded,
              [dayKey]: !isAfterExpanded,
            }));
            if (!isDayExpanded && onDayExpand) {
              onDayExpand();
            }
          } else {
            dispatch(setIsAfterRecurringSectionExpanded(!isAfterExpanded));
          }
        }}
        viewType={viewType}
        addPaddingLeft={((isWeekView
          && String(weekViewHorizontallyExpandedDay) === dayKey))}
      >
        <IconContainerWrapper>
          <IcomoonReact
            iconSet={iconSet}
            size={15}
            icon='recurring-circle'
            color='white'
          />
        </IconContainerWrapper>
        <LineContainer>
          <RecurringText>
            Recurring (
            {afterRecurringWorkouts[formatDate(effectiveDate)].length}
            )
          </RecurringText>
        </LineContainer>
        {(viewType === 'dayView' || (viewType === 'weekView' && isRecurringRowInHorizontallyExpandedDay)) && (
        <ExpandCollapseContainer
          onMouseEnter={() => setAfterExpandHovering(true)}
          onMouseLeave={() => setAfterExpandHovering(false)}
          onClick={() => {
            if (viewType === 'weekView') {
              dispatch(setIsAfterRecurringSectionExpanded({
                ...isAfterExpanded,
                [dayKey]: !isAfterExpanded,
              }));
            } else {
              dispatch(setIsAfterRecurringSectionExpanded(!isAfterExpanded));
            }
          }}
          data-tooltip-id={afterTooltipId}
        >
          <IcomoonReact
            iconSet={iconSet}
            size={15}
            icon={isAfterExpanded ? 'collapse' : 'expand2'}
            color={afterExpandHovering ? '#77A7FF' : 'lightgray'}
          />
          <ReactTooltip
            id={afterTooltipId}
            place='left'
            content={isAfterExpanded ? 'Collapse' : 'Expand'}
            style={{
              backgroundColor: 'rgba(68, 68, 68, 1)',
              color: 'white',
              fontFamily: 'Nunito Sans',
              fontSize: '13px',
              zIndex: 1000,
            }}
          />
        </ExpandCollapseContainer>
        )}
      </IconContainer>
      <BeforeRecurringWorkouts>
        <div>
          {afterRecurringWorkouts[formatDate(effectiveDate)]
            ? afterRecurringWorkouts[formatDate(effectiveDate)].map((recurringWorkout, idx) => (
              <div key={recurringWorkout.assignedId || idx}>
                <DraggableContentWrapper
                  recurringWorkout={recurringWorkout}
                  idx={idx}
                  beforeOrAfter='after'
                  measure={measure}
                  isAfterExpanded={isAfterExpanded}
                  cache={cache}
                  date={date}
                  onDayExpand={onDayExpand}
                  isDayExpanded={isDayExpanded}
                />
              </div>
            ))
            : null}
        </div>
      </BeforeRecurringWorkouts>
    </AfterRecurringHeader>
  );
});

AfterRecurringVirtualRow.defaultProps = {
  measure: () => {},
  cache: {},
  date: null,
  dayKey: null,
  isDayExpanded: false,
  onDayExpand: () => {},
};

AfterRecurringVirtualRow.propTypes = {
  measure: PropTypes.func,
  cache: PropTypes.instanceOf(CellMeasurerCache),
  date: PropTypes.string,
  dayKey: PropTypes.string,
  isDayExpanded: PropTypes.bool,
  onDayExpand: PropTypes.func,
};

export default AfterRecurringVirtualRow;
